import { useCallback } from "react";
import useSWR from "swr";
import { Paginated, PaginationQuery, UpdateOrderRequestBody, UpdateVisibilityRequestBody } from "../../types";
import { buildUrl, mutateCache, useApi } from "../api";
import { CustomError } from "../errors";
import { InsertShuttleBody, Shuttle, UpdateShuttleBody } from "./types/shuttles";

export const useShuttlesApi = () => {
  const api = useApi();

  const useAdminSystemTypeShuttles = useCallback(
    (systemTypeId: string, query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl(`/admin/settings/vertigrip/system_types/${systemTypeId}/shuttles`, query);
      return useSWR<Paginated<Shuttle>, CustomError, string>(url, (url) =>
        api.get<Paginated<Shuttle>>({ url, signal }),
      );
    },
    [api],
  );

  const insertAdminShuttle = useCallback(
    async (body: InsertShuttleBody): Promise<{ id: string }> => {
      const url = `/admin/settings/vertigrip/shuttles`;
      const id = await api.post<{ id: string }, InsertShuttleBody>(url, body);
      mutateCache([url, `/admin/settings/vertigrip/system_types/${body.systemTypeId}/shuttles`]);
      return id;
    },
    [api],
  );

  const updateAdminShuttle = useCallback(
    async (shuttleId: string, body: UpdateShuttleBody): Promise<void> => {
      const url = `/admin/settings/vertigrip/shuttles/${shuttleId}`;
      (await api.put<UpdateShuttleBody>(url, body),
        mutateCache([
          `/admin/settings/vertigrip/shuttles`,
          `/admin/settings/vertigrip/system_types/${body.systemTypeId}/shuttles`,
        ]));
    },
    [api],
  );

  const deleteAdminShuttle = useCallback(
    async (shuttleId: string, systemTypeId: string): Promise<void> => {
      const url = `/admin/settings/vertigrip/shuttles/${shuttleId}`;
      await api.del(url);
      mutateCache([
        `/admin/settings/vertigrip/shuttles`,
        `/admin/settings/vertigrip/shuttles/${shuttleId}`,
        `/admin/settings/vertigrip/system_types/${systemTypeId}/shuttles`,
      ]);
    },
    [api],
  );

  const updateAdminShuttleVisibility = useCallback(
    async (
      shuttleId: string,
      visibility: UpdateVisibilityRequestBody,
      options?: { systemTypeId?: string },
    ): Promise<void> => {
      const url = `/admin/settings/vertigrip/shuttles/${shuttleId}/visibility`;
      await api.put<UpdateVisibilityRequestBody>(url, visibility);
      mutateCache([
        "/admin/settings/vertigrip/shuttles",
        `/admin/settings/vertigrip/system_types/${options?.systemTypeId}/shuttles`,
      ]);
    },
    [api],
  );

  const updateAdminShuttleOrder = useCallback(
    async (shuttleId: string, order: UpdateOrderRequestBody, options?: { systemTypeId?: string }): Promise<void> => {
      const url = `/admin/settings/vertigrip/shuttles/${shuttleId}/order`;
      await api.put<UpdateOrderRequestBody>(url, order);
      mutateCache([
        "/admin/settings/vertigrip/shuttles",
        `/admin/settings/vertigrip/system_types/${options?.systemTypeId}/shuttles`,
      ]);
    },
    [api],
  );

  return {
    useAdminSystemTypeShuttles,
    insertAdminShuttle,
    updateAdminShuttle,
    updateAdminShuttleVisibility,
    updateAdminShuttleOrder,
    deleteAdminShuttle,
  };
};
